// src/App.jsx
import { useEffect, useRef, useState } from "react";
import {
  onAuthStateChanged,
  signOut,
  sendEmailVerification,
  signInWithEmailAndPassword,
  getMultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
} from "firebase/auth";
import { collection, query, where, getDocs, doc, setDoc, getDoc, onSnapshot, serverTimestamp, limit as fsLimit } from "firebase/firestore";
import { db } from "./firebase.js";
import { auth } from "./firebase.js";
import LoginPage from "./LoginPage";
import Dashboard from "./Dashboard";
import BudgetPage from "./BudgetPage";
import BudgetBucketsPage from "./BudgetBucketsPage";
import Sidebar from "./Sidebar";
import Reports from "./Reports";
import Transactions from "./Transactions"; // ✅ Use real version
// import TransactionRowWireframe from "./TransactionRowWireframe"; // ❌ No longer needed
import "react-datepicker/dist/react-datepicker.css";
import DatePicker from "react-datepicker";
// src/App.jsx
import WizardChoice from "./wizard/WizardChoice";         
import WizardShell from "./wizard/WizardShell";             
import { userHasAnyBudgetAmounts } from "./lib/budgetChecks"; 
// ↓ Add this import if you created the Rules page in Step 2
import RulesPage from "./rulesPage";
import GoalsPage from "./GoalsPage";
// at top of src/App.jsx (with the other imports)
// at top of src/App.jsx (with the other imports)
import { BrowserRouter as Router, useLocation, useNavigate } from "react-router-dom";
import SettingsPage from "./SettingsPage";
import useAuthUser from "./useAuthUser.js";
import AcceptInvitePage from "./AcceptInvitePage.jsx";
import AuthAssist from "./AuthAssist.jsx";
import EmailPreview from "./EmailPreview";
import TxActionDemo from "./TxActionDemo.jsx";
import StickyFooter from "./components/StickyFooter";        
import FeedbackInbox from "./FeedbackInbox";
import { multiFactor } from "firebase/auth"; 
import { firebaseConfig } from "./firebase"; 
import SignUpChoice from "./SignUpChoice";
import SignUpBeta from "./SignUpBeta";
import SignUpMFA from "./SignUpMFA";
import OnboardingBanner from "./OnboardingBanner";
import { SETUP_STEPS } from "./onboardingConfig";
import TermsPage from "./TermsPage";
import TermsConsent from "./TermsConsent";
import { CURRENT_TERMS_VERSION } from "./termsConfig";
import PrivacyPage from "./PrivacyPage";

// keep all your existing imports (including Router and useLocation)

// --- Plaid OAuth (endpoints used for auto-resume) ---
const PLAID_CREATE_URL  = "https://us-central1-trelli-prod-473722.cloudfunctions.net/createLinkToken";
const PLAID_EXCHANGE_URL = "https://us-central1-trelli-prod-473722.cloudfunctions.net/exchangePublicToken";

// 1) Move your current App body into AppInner (this is the one that uses useLocation)
function AppInner() {
  const [loggedIn, setLoggedIn] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [bootstrapReady, setBootstrapReady] = useState(false);
  const [view, setView] = useState("dashboard");
  const [checkedFresh, setCheckedFresh] = useState(false);
  const [hasBudget, setHasBudget] = useState(null);
  const [checkingBudget, setCheckingBudget] = useState(true);
  const { user: mfaUser, loading: mfaLoading, hasMFA, emailVerified } = useAuthUser();
  const notifyInFlightRef = useRef(false);

  // ---- Global normalization rebuild banner (follows navigation) ----
  const [normAccountId, setNormAccountId] = useState(null);
  const [normJob, setNormJob] = useState(null);
  const [normMinimized, setNormMinimized] = useState(false);
  const [normLastTick, setNormLastTick] = useState(Date.now());

  // --- Inactivity Auto-Logout (configurable) ---
  const IDLE_MS = 5 * 60 * 1000;        // 5 minutes of no activity → show modal
  const COUNTDOWN_SECONDS = 60;         // modal countdown before auto-logout

  // Total idle window (grace included)
  const IDLE_TOTAL_MS = IDLE_MS + COUNTDOWN_SECONDS * 1000;

  // Keys: per-user (for precision) + global (so pre-React guards can read it)
  const idleKeyFor = (uid) => `trelli:idleDeadline:${uid || "anon"}`;
  const GLOBAL_IDLE_KEY = "trelli:idleDeadline:global";

  function setIdleDeadline(uid, msFromNow = IDLE_TOTAL_MS) {
    const ts = Date.now() + msFromNow;
    try {
      localStorage.setItem(idleKeyFor(uid), String(ts)); // per-user
      localStorage.setItem(GLOBAL_IDLE_KEY, String(ts)); // global (index.html/main.jsx can read)
    } catch {}
  }

  function getIdleDeadline(uid) {
    try {
      const s = localStorage.getItem(idleKeyFor(uid));
      return s ? Number(s) : null;
    } catch {
      return null;
    }
  }

  function clearIdleDeadline(uid) {
    try {
      localStorage.removeItem(idleKeyFor(uid)); // per-user
      localStorage.removeItem(GLOBAL_IDLE_KEY); // global
    } catch {}
  }

  const [lastActiveAt, setLastActiveAt] = useState(Date.now());
  const [idleModalOpen, setIdleModalOpen] = useState(false);
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);

  async function callNotifyNewSignup() {
    try {
      const u = auth.currentUser;
      if (!u) return;
      const idToken = await u.getIdToken(/* force */ true);
      await fetch("https://us-central1-trelli-prod-473722.cloudfunctions.net/notifyNewSignup", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({}),
      });
    } catch (e) {
      console.warn("[notifyNewSignup] skipped:", e?.message || e);
    }
  }

  // Record activity (any user interaction counts)
  const markActive = () => {
    if (idleModalOpen) return;
    setLastActiveAt(Date.now());
    const uid = auth.currentUser?.uid;
    if (uid) setIdleDeadline(uid); // persist across tab closes / device sleep
  };

  // Router navigation
  const navigate = useNavigate();
  const ONBOARDING_LOCK_ENABLED = false; // ⛔ disable step-based redirects

    // 🔗 Global navigation bridge (lets any component jump views immediately)
    useEffect(() => {
      const onNavigate = (ev) => {
        const detail = ev?.detail;
        // Allow both a simple string ("settings") or an object { view, path, tab }
        const viewTarget =
          typeof detail === "string" ? detail : (detail?.view || null);

        if (viewTarget) setView(viewTarget);

        // Keep the URL in sync (optional path override supported)
        if (typeof detail === "object" && detail?.path) {
          navigate(detail.path);
        } else if (viewTarget) {
          // basic mapping by view name
          const path =
            viewTarget === "dashboard"   ? "/" :
            viewTarget === "transactions"? "/transactions" :
            viewTarget === "budget"      ? "/budget" :
            viewTarget === "reports"     ? "/reports" :
            viewTarget === "rules"       ? "/rules" :
            viewTarget === "goals"       ? "/goals" :
            viewTarget === "settings"    ? "/settings" :
            "/";
          navigate(path);
        }
      };

      window.addEventListener("trelli:navigate", onNavigate);
      return () => window.removeEventListener("trelli:navigate", onNavigate);
    }, [navigate]);

  // Live user profile from Firestore (for onboarding flags, plaidConnected, etc.)
  const [userDoc, setUserDoc] = useState(null);
  const [userDocReady, setUserDocReady] = useState(false);

  useEffect(() => {
    const u = auth.currentUser;
    if (!u?.uid) return;

    const key = `trelli:activeNormalizationRebuild:${u.uid}`;

    const loadFromStorage = () => {
      try {
        const acct = localStorage.getItem(key);
        if (acct) setNormAccountId(acct);
      } catch {}
    };

    // Load immediately on mount
    loadFromStorage();

    // Listen for the SettingsPage "job started" event
    const onStarted = () => loadFromStorage();
    window.addEventListener("trelli:activeNormalizationRebuild", onStarted);

    // Also listen for storage events (helps if multiple tabs)
    const onStorage = (e) => {
      if (e.key === key) loadFromStorage();
    };
    window.addEventListener("storage", onStorage);

    return () => {
      window.removeEventListener("trelli:activeNormalizationRebuild", onStarted);
      window.removeEventListener("storage", onStorage);
    };
  }, [loggedIn]);

  useEffect(() => {
    const u = auth.currentUser;
    if (!u?.uid || !normAccountId) return;

    const key = `trelli:activeNormalizationRebuild:${u.uid}`;

    // IMPORTANT: this matches your job schema path
    const jobRef = doc(db, "users", u.uid, "jobs", "normalizationRebuilds", "byId", normAccountId);

    const unsub = onSnapshot(
      jobRef,
      (snap) => {
        if (!snap.exists()) return;
        const data = snap.data();
        setNormJob(data);
        setNormLastTick(Date.now());

        const done = data?.status === "completed" || data?.status === "failed";
        if (done) {
          // Clear persisted job so banner auto-closes everywhere
          try { localStorage.removeItem(key); } catch {}
          setTimeout(() => {
            setNormAccountId(null);
            setNormJob(null);
            setNormMinimized(false);
          }, 800);
        }
      },
      (err) => {
        console.warn("[norm banner] job snapshot failed:", err?.message || err);
      }
    );

    return () => unsub();
  }, [normAccountId]);

  useEffect(() => {
    if (!loggedIn || !auth.currentUser) {
      setUserDoc(null);
      setUserDocReady(false);
      return;
    }
    const ref = doc(db, "users", auth.currentUser.uid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setUserDoc(snap.exists() ? snap.data() : {});
        setUserDocReady(true);
      },
      () => setUserDocReady(true)
    );
    return () => unsub();
  }, [loggedIn]);

  // --- Mobile breakpoint helper (matches Sidebar’s 768px) ---
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 768px)").matches : false
  );

  // One-shot: on first successful login for this user, stamp firstLoginAt and send alert email
  useEffect(() => {
    (async () => {
      // Must be logged in, have a Firebase user, and have loaded the userDoc
      if (!loggedIn || !auth.currentUser || !userDocReady) return;
      if (!userDoc) return;

      // If the server already stamped, skip immediately
      if (userDoc.signupNotifiedAt) return;

      // Per-session guard (avoid repeats from StrictMode/rerenders in this tab)
      const uid = auth.currentUser.uid;
      const sessionKey = `__signupNotifySent:${uid}`;
      if (sessionStorage.getItem(sessionKey) === "1") return;

      // In-flight guard (avoid overlapping calls within this render)
      if (notifyInFlightRef.current) return;

      notifyInFlightRef.current = true;
      try {
        const ref = doc(db, "users", uid);

        // Ensure firstLoginAt exists (set only if missing)
        if (!userDoc.firstLoginAt) {
          await setDoc(ref, { firstLoginAt: serverTimestamp() }, { merge: true });
        }

        // Call backend; server uses a transaction + deterministic /mail id to be truly idempotent
        await callNotifyNewSignup();

        // Mark this tab/session as having sent it
        sessionStorage.setItem(sessionKey, "1");
      } catch (e) {
        console.warn("[first-login notify] failed:", e?.message || e);
      } finally {
        notifyInFlightRef.current = false;
      }
    })();
  }, [loggedIn, userDocReady, userDoc]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(max-width: 768px)");
    const onChange = (e) => setIsMobile(e.matches);
    try { mql.addEventListener("change", onChange); } catch { mql.addListener(onChange); }
    return () => {
      try { mql.removeEventListener("change", onChange); } catch { mql.removeListener(onChange); }
    };
  }, []);

  // --- NEW: "fresh" snapshot of the live Firebase user ---
  const [freshEmailVerified, setFreshEmailVerified] = useState(false);
  const [freshMfaCount, setFreshMfaCount] = useState(0);

  const latestRefreshRef = useRef(0);

  // Listen for real activity: clicks, keys, mouse, touch, scroll, and visibility
  useEffect(() => {
    const events = ["click", "keydown", "mousemove", "touchstart", "scroll"];
    events.forEach((ev) => window.addEventListener(ev, markActive, { passive: true }));

    // When the tab becomes visible again, mark active
    const onVis = () => {
      if (document.visibilityState === "visible") markActive();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, markActive));
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [idleModalOpen]); // rebind if modal state changes

  // Check for inactivity; when idle, open modal and start countdown
  useEffect(() => {
    if (!auth.currentUser) return; // only when logged in
    let timer = setInterval(() => {
      const idleFor = Date.now() - lastActiveAt;
      if (!idleModalOpen && idleFor >= IDLE_MS) {
        setIdleModalOpen(true);
        setCountdown(COUNTDOWN_SECONDS);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [lastActiveAt, idleModalOpen]);

  // While the modal is open, tick the countdown; when it hits 0, sign out
  useEffect(() => {
    if (!idleModalOpen) return;
    if (!auth.currentUser) return;

    let t = setInterval(async () => {
      setCountdown((s) => {
        if (s <= 1) {
          clearInterval(t);
          // Auto-logout
            const uid = auth.currentUser?.uid || localStorage.getItem("trelli:lastUid");
            signOut(auth).catch(() => {}).finally(() => {
              if (uid) clearIdleDeadline(uid);
              try { localStorage.removeItem("trelli:lastUid"); } catch {}
            });
          return 0;
        }
        return s - 1;
      });
    }, 1000);

    return () => clearInterval(t);
  }, [idleModalOpen]);

  async function readMfaStatusFresh() {
    const u = auth.currentUser;
    if (!u) return { emailVerified: false, mfaCount: 0 };

    // Force-refresh account + token
    try { await u.reload(); } catch {}
    let mfaCount = (multiFactor(u)?.enrolledFactors || []).length;

    // If still 0, force a fresh ID token and inspect claims
    if (mfaCount === 0) {
      try {
        const idTok = await u.getIdToken(/* forceRefresh */ true);
        // eslint-disable-next-line no-unused-vars
        const _ = idTok; // just to avoid linter warnings
        const res = await u.getIdTokenResult(); // parsed claims
        const claims = res?.claims || {};
        // These claims appear when a second factor has been used / is present
        if (claims.sign_in_second_factor || claims.second_factor_identifier) {
          mfaCount = 1; // treat as at least one factor
        }
      } catch {}
    }

    return {
      emailVerified: !!u.emailVerified,
      mfaCount,
    };
  }

  // One-shot refetch of the *live* Firebase user (ensures MFA + emailVerified are current)
  // Order of truth: user.reload() → enrolledFactors → ID token claims → REST lookup
  async function refreshAuthSnapshot() {
    const u = auth.currentUser;
    console.debug("[MFA REFRESH] start", { hasUser: !!u });

    if (!u) {
      setFreshEmailVerified(false);
      setFreshMfaCount(0);
      console.debug("[MFA REFRESH] no user → set mfa=0, emailVerified=false");
      return;
    }

    try {
      await u.getIdToken(true);   // force-refresh claims
      await u.reload();           // refresh profile/enrolledFactors
    } catch (e) {
      console.debug("[MFA REFRESH] reload/getIdToken failed", e?.message || e);
    }

    // Primary: direct enrolled factors
    let mfaCount = (u.multiFactor?.enrolledFactors || []).length || 0;
    console.debug("[MFA REFRESH] after reload enrolledFactors =", mfaCount);

    // Fallback 1: ID token claims
    if (mfaCount === 0) {
      try {
        const res = await u.getIdTokenResult(true);
        const claims = res?.claims || {};
        if (claims.sign_in_second_factor || claims.second_factor_identifier) {
          mfaCount = 1;
          console.debug("[MFA REFRESH] claims indicate MFA; set mfaCount=1");
        } else {
          console.debug("[MFA REFRESH] no MFA claims present");
        }
      } catch (e) {
        console.debug("[MFA REFRESH] getIdTokenResult failed", e?.message || e);
      }
    }

    // Fallback 2: authoritative REST lookup
    if (mfaCount === 0) {
      try {
        const viaRest = await fetchMfaViaRest();
        console.debug("[MFA REFRESH] REST lookup mfaCount =", viaRest);
        if (viaRest > 0) mfaCount = viaRest;
      } catch (e) {
        console.debug("[MFA REFRESH] REST lookup failed", e?.message || e);
      }
    }

    setFreshEmailVerified(!!u.emailVerified);
    setFreshMfaCount(mfaCount);
    console.debug("[MFA REFRESH] applied", { emailVerified: !!u.emailVerified, mfaCount });
  }

  // Compute MFA from the live Firebase user as a fallback
  const hasMFAComputed = (auth.currentUser?.multiFactor?.enrolledFactors?.length || 0) > 0;
  const mfaReady = hasMFA || hasMFAComputed;

  const isDev = import.meta.env.DEV;

  async function readMfaCount(auth) {
    const u = auth.currentUser;
    if (!u) return 0;
    try { await u.reload(); } catch (_) {}
    const factors = multiFactor(u)?.enrolledFactors || [];
    return factors.length || 0;
  }

  // Ensure the user doc exists but DO NOT create a household here.
  // AcceptInvitePage sets activeHouseholdId when invitees accept.
  async function ensureHouseholdContext(u) {
    if (!u) return;
    try {
      const userRef = doc(db, "users", u.uid);
      const snap = await getDoc(userRef);
      const existing = snap.exists() ? snap.data() : null;

      // If they already have an active household, do nothing.
      if (existing?.activeHouseholdId) return;

      // Create/merge a minimal user profile (no household creation here).
      await setDoc(
        userRef,
        {
          email: u.email || null,
          // keep any existing role; default to "primary" if none
          role: existing?.role || "primary",
          updatedAt: serverTimestamp(),
          // createdAt only if it's brand new
          ...(snap.exists() ? {} : { createdAt: serverTimestamp() }),
        },
        { merge: true }
      );
    } catch (e) {
      console.warn("[ensureHouseholdContext] skipped:", e?.message || e);
    }
  }

  // Fallback: fetch MFA enrollments directly from Google Identity Toolkit
  async function fetchMfaViaRest() {
    const u = auth.currentUser;
    if (!u) return 0;

    // Always get a fresh token so backend returns current enrollments
    const idToken = await u.getIdToken(true);

    const resp = await fetch(
      `https://identitytoolkit.googleapis.com/v1/accounts:lookup?key=${firebaseConfig.apiKey}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken }),
      }
    );

    if (!resp.ok) {
      // If this fails, just say 0; we’ll have other fallbacks
      return 0;
    }

    const data = await resp.json();
    const infos = data?.users?.[0]?.mfaInfo || [];
    return Array.isArray(infos) ? infos.length : 0;
  }

  // ——— Onboarding helpers ———
  function hasMfaEnrolled(firebaseUser, userDoc) {
    const authHas = !!(firebaseUser?.multiFactor?.enrolledFactors || []).length;
    const docHas  = userDoc?.mfaEnrolled === true;
    return authHas || docHas;
  }

  async function advanceSetup(toKey) {
    if (!auth.currentUser) return;
    const ref = doc(db, "users", auth.currentUser.uid);
    await setDoc(
      ref,
      { onboarding: { ...(userDoc?.onboarding || {}), setupStep: toKey, updatedAt: new Date() } },
      { merge: true }
    );
  }

  function requiredRouteFor(stepKey) {
    if (!ONBOARDING_LOCK_ENABLED) {
      // don’t force navigation anywhere when lock is off
      return (location.pathname || "/") + (location.search || "");
    }
    const step = SETUP_STEPS.find((s) => s.key === stepKey);
    return step?.route || "/settings?tab=account";
  }

  // --- NEW: when we become logged-in, fetch a fresh live snapshot once ---
  useEffect(() => {
    if (loggedIn) {
      refreshAuthSnapshot();
    } else {
      setFreshEmailVerified(false);
      setFreshMfaCount(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn]);

  // After login, briefly poll for fresh MFA/emailVerified (handles slow claim propagation)
  useEffect(() => {
    if (!loggedIn) return;
    let count = 0;
    const id = setInterval(() => {
      count += 1;
      refreshAuthSnapshot();
      if (count >= 8) clearInterval(id); // ~8 seconds
    }, 1000);
    return () => clearInterval(id);
  }, [loggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  // When the tab becomes visible again, grab a fresh snapshot
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible") {
        refreshAuthSnapshot();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []); // run once

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (auth.currentUser) {
          // Force-refresh the live Firebase user so emailVerified/MFA are up to date
          await auth.currentUser.reload();
        }
      } catch {}
      if (mounted) setCheckedFresh(true);
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log("AUTH: onAuthStateChanged fired. user?", !!user); // <-- moved inside where 'user' exists
      setLoggedIn(!!user);
      
      // If a previous session set a forceSignOut flag, only honor it when NOT in auth flow.
      // During MFA / fresh login we ignore and clear it so we don’t bounce.
      try {
        const force = sessionStorage.getItem("trelli:forceSignOut") === "1";
        const inAuth = sessionStorage.getItem("trelli:authFlow") === "1";

        if (force && user) {
          if (inAuth) {
            // We’re mid-auth (e.g., returning from SMS). Clear and continue.
            sessionStorage.removeItem("trelli:forceSignOut");
          } else {
            // Not in auth flow → honor the force sign-out.
            sessionStorage.removeItem("trelli:forceSignOut");
            await signOut(auth);
            return; // stop bootstrapping this session
          }
        }
      } catch {}

      // Track last signed-in uid for cold-boot idle enforcement
      try {
        if (user) {
          localStorage.setItem("trelli:lastUid", user.uid);
        } else {
          localStorage.removeItem("trelli:lastUid");
        }
      } catch {}

      setCheckingAuth(false);

      if (user) {
        // Give this fresh login a short grace window so stale deadlines don’t kick immediately.
        try {
          const GRACE_MS = 8000; // ~8s is plenty for post-MFA settling on iOS/Chrome
          sessionStorage.setItem("trelli:loginGraceUntil", String(Date.now() + GRACE_MS));
        } catch {}

        try { sessionStorage.removeItem("trelli:authFlow"); } catch {}

        // we’re signed in; block pages until bootstrap is done
        setBootstrapReady(false);

        // ── Bootstrap membership so Firestore rules allow reads ───────────────
        try {
          const uid = user.uid;

          // 1) Upsert /households/{uid}/members/{uid} as head
          const memberRef = doc(db, "households", uid, "members", uid);
          await setDoc(
            memberRef,
            { role: "head", joinedAt: serverTimestamp() },
            { merge: true }
          );

          // 2) Upsert /households/{uid}
          const hhRef = doc(db, "households", uid);
          await setDoc(
            hhRef,
            { createdBy: uid, updatedAt: serverTimestamp() },
            { merge: true }
          );
        } catch (e) {
          if (import.meta.env.DEV) {
            console.debug("[bootstrap household] info:", e?.code || e?.message || e);
          }
        }
        // ──────────────────────────────────────────────────────────────────────

        // seed a fresh idle deadline
        try { setIdleDeadline(user.uid); } catch {}

        // mark bootstrap complete BEFORE we query anything else
        setBootstrapReady(true);

        // …your existing budget check stays AFTER this…
        try {
          setCheckingBudget(true);
          const hasAny = await userHasAnyBudgetAmounts(user.uid);
          setHasBudget(hasAny);
        } catch (e) {
          console.error("Error checking budget amounts:", e);
          setHasBudget(false);
        } finally {
          setCheckingBudget(false);
        }
      } else {
        setHasBudget(false);
        setCheckingBudget(false);
        setBootstrapReady(false); // reset when logged out
      }
    });
    return () => unsubscribe();
  }, []);

  // Seed/check idle deadline whenever auth state flips
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) {
      clearIdleDeadline();
      return;
    }

    const now = Date.now();
    let graceUntil = 0;
    let inAuth = false;
    try { graceUntil = Number(sessionStorage.getItem("trelli:loginGraceUntil")) || 0; } catch {}
    try { inAuth = sessionStorage.getItem("trelli:authFlow") === "1"; } catch {}

    const dl = getIdleDeadline(uid);
    const expired = dl && now > dl;

    if (expired) {
      // If we just logged in or are still in the auth flow, treat the old deadline as stale.
      if (inAuth || now < graceUntil) {
        setIdleDeadline(uid);  // reset fresh
      } else {
        // Truly expired outside auth context → sign out
        signOut(auth).catch(() => {});
        return;
      }
    } else {
      // Not expired → ensure we have a fresh deadline
      setIdleDeadline(uid);
    }
  }, [loggedIn]);

  // On visibility *or* bfcache restore, enforce deadline
  useEffect(() => {
    const enforce = () => {
      const uid = auth.currentUser?.uid;
      if (!uid) return;
      const dl = getIdleDeadline(uid);
      if (dl && Date.now() > dl) {
        signOut(auth).catch(() => {}).finally(() => {
        clearIdleDeadline(uid);
        try { localStorage.removeItem("trelli:lastUid"); } catch {}
      });
      }
    };

    const onVis = () => {
      if (document.visibilityState === "visible") enforce();
    };

    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pageshow", enforce); // always enforce on pageshow (incl. bfcache)

    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pageshow", enforce);
    };
  }, []);

  // Lightweight periodic guard (runs only when logged in; cheap check)
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;

    const id = setInterval(() => {
      let inAuth = false;
      let graceUntil = 0;
      try { inAuth = sessionStorage.getItem("trelli:authFlow") === "1"; } catch {}
      try { graceUntil = Number(sessionStorage.getItem("trelli:loginGraceUntil")) || 0; } catch {}

      // Suppress enforcement during MFA or initial post-login grace.
      if (inAuth || Date.now() < graceUntil) return;

      const dl = getIdleDeadline(uid);
      if (dl && Date.now() > dl) {
        clearInterval(id);
        signOut(auth).catch(() => {}).finally(() => {
          clearIdleDeadline(uid);
        });
      }
    }, 15000);

    return () => clearInterval(id);
  }, [loggedIn]);

  // Reset inactivity state whenever auth state changes (prevents stale countdown after re-login)
  useEffect(() => {
    // Close modal, reset countdown, and restart the idle clock on both login and logout
    setIdleModalOpen(false);
    setCountdown(COUNTDOWN_SECONDS);
    setLastActiveAt(Date.now());
  }, [loggedIn]);

  useEffect(() => {
    window.__trelliStartWizard = () => setView("wizard");
  }, []);

  // 🔍 DEBUG: expose helpers to watch transactions live from console
  useEffect(() => {
    if (!auth.currentUser) return;

    // Watch a single transaction by id:
    //   in console: window.__txWatch("<docId>")
    window.__txWatch = (txId) => {
      if (!txId) return console.warn("__txWatch requires a doc id");
      const ref = doc(db, "transactions", txId);
      console.group(`[__txWatch] attach ${txId}`);
      console.log("user:", auth.currentUser.uid);
      console.groupEnd();
      return onSnapshot(ref, (snap) => {
        const d = snap.data();
        console.group(`[__txWatch] change ${txId}`);
        console.log("exists:", snap.exists());
        console.table({
          status: d?.status,
          needsReview: d?.needsReview,
          category: d?.category,
          matchedCategory: d?.categoryMatch?.category,
          matchedSubcategory: d?.categoryMatch?.subcategory,
          matchedSource: d?.categoryMatch?.source,
          source: d?.source,
          type: d?.type,
          updatedAt: d?.updatedAt || null,
        });
        console.log("raw:", d);
        console.groupEnd();
      });
    };

    // Watch *recent* transactions (default last 7 days), client-side filtered to avoid composite index
    //   in console: window.__txWatchRecent()            // 7 days, cap 500
    //               window.__txWatchRecent(30, 1000)    // 30 days, cap 1000
    window.__txWatchRecent = (days = 7, cap = 500) => {
      const since = new Date(Date.now() - days * 86400000);
      // Query ONLY by userId and cap results → no composite index needed
      const qRef = query(
        collection(db, "transactions"),
        where("userId", "==", auth.currentUser.uid),
        fsLimit(cap)
      );
      console.group("[__txWatchRecent] attach");
      console.log("since:", since.toISOString(), "cap:", cap);
      console.groupEnd();
      return onSnapshot(qRef, (snap) => {
        snap.docChanges().forEach((chg) => {
          const d = chg.doc.data();
          // client-side date filter (avoids index on userId+date)
          const ts = d?.date?.toDate ? d.date.toDate() : d?.date;
          if (ts && ts < since) return;

          console.group(`[__txWatchRecent] ${chg.type} ${chg.doc.id}`);
          console.table({
            status: d?.status,
            needsReview: d?.needsReview,
            category: d?.category,
            matchedCategory: d?.categoryMatch?.category,
            matchedSubcategory: d?.categoryMatch?.subcategory,
            matchedSource: d?.categoryMatch?.source,
          });
          console.groupEnd();
        });
      });
    };

  }, [loggedIn]); // rebind when auth state changes

  // Always create location and URL params (hook must be unconditional)
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const authMode = (params.get("auth") || "").toLowerCase();
  const showAuthAssist = authMode === "signin" || authMode === "signup";
  const showAcceptInvite = location.pathname.startsWith("/accept-invite");
  const showTxDemo = location.pathname.startsWith("/tx-action-demo");

  // Keep this route→view sync hook above any returns
  useEffect(() => {
    if (view === "wizard" || view === "wizard_choice") return;
    const p = location.pathname || "/";
    if (p.startsWith("/goals")) setView("goals");
    else if (p.startsWith("/rules")) setView("rules");
    else if (p.startsWith("/transactions")) setView("transactions");
    else if (p.startsWith("/budget")) setView("budget");
    else if (p.startsWith("/reports")) setView("reports");
    else if (p.startsWith("/feedback")) setView("feedback");
    else if (p.startsWith("/settings")) setView("settings");
    else setView("dashboard");
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // ——— Hard gate: initial setup orchestration ———
  useEffect(() => {
    // 🔕 completely disable setup-step routing while we test
    if (!ONBOARDING_LOCK_ENABLED) return;

    if (!loggedIn || !userDocReady) return;

    // If you’re on the public Terms page, do not run any onboarding redirects
    if (location.pathname.startsWith("/terms")) {
      return; // no gating on Terms page, ever
    }

    // ——— Legal gate: must accept current Terms version before setup steps ———
    const v = userDoc?.legal?.termsVersionAccepted || null;
    const isOnTermsPages =
      location.pathname.startsWith("/signup/terms") ||
      location.pathname.startsWith("/terms");

    if (v !== CURRENT_TERMS_VERSION) {
      if (!isOnTermsPages) {
        navigate("/signup/terms", { replace: true });
      }
      return; // pause other gating until accepted
    }

    const onboarding = userDoc?.onboarding || {};
    let setupStep = onboarding.setupStep || null;

    // Auto-advance when conditions are satisfied
    if (setupStep === "mfa" && hasMfaEnrolled(auth.currentUser, userDoc)) {
      advanceSetup("plaid");
      setupStep = "plaid";
    }
    if (setupStep === "plaid" && userDoc?.plaidConnected === true) {
      advanceSetup("notifications");
      setupStep = "notifications";
    }

    // During setup, force the user onto the required Settings tab (route + view)
    if (setupStep && setupStep !== "done") {
      const mustBe = requiredRouteFor(setupStep);
      const here = (location.pathname || "/") + (location.search || "");
      if (here !== mustBe) {
        navigate(mustBe, { replace: true });
        // also sync 'view' so your panel renders Settings immediately
        if (view !== "settings") {
          // push the settings view so the shell shows the right page
          // (the route watcher will also switch view on next tick)
          // but forcing it now prevents a brief flash
          // eslint-disable-next-line no-undef
          setView("settings");
        }
      }
      return; // keep the lock until this step flips complete
    }

    // When setup finishes (notifications ack), start the feature tour (if not decided yet)
    if (setupStep === "notifications" && onboarding?.ackNotifications === true) {
      (async () => {
        await advanceSetup("done");
        if (!["completed", "dismissed"].includes(onboarding.featuresTour)) {
          await setDoc(
            doc(db, "users", auth.currentUser.uid),
            { onboarding: { ...onboarding, featuresTour: "active", featureStep: "dashboard" } },
            { merge: true }
          );
          if (location.pathname !== "/dashboard") {
            navigate("/dashboard", { replace: true });
            if (view !== "dashboard") setView("dashboard");
          }
        }
      })();
    }
  // include everything this logic depends on:
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn, userDocReady, userDoc, location.pathname, location.search]);

  // One-time cleanup of old localStorage keys
  useEffect(() => {
    ["viewMode", "goalsComplete", "wizardStep"].forEach((k) => {
      if (localStorage.getItem(k) !== null) localStorage.removeItem(k);
    });
  }, []);

  // After we know a user is signed in, make sure the user doc exists (no household creation)
  useEffect(() => {
    if (!checkingAuth && loggedIn && auth.currentUser) {
      ensureHouseholdContext(auth.currentUser);
    }
  }, [checkingAuth, loggedIn]);

  const handleSelect = async (selected) => {
    if (selected === "logout") {
      const uid = auth.currentUser?.uid || localStorage.getItem("trelli:lastUid");
      try { await signOut(auth); } finally {
        try { localStorage.removeItem("trelli:lastUid"); } catch {}
        if (uid) clearIdleDeadline(uid);
      }
      setLoggedIn(false);
      setView("dashboard");
      return;
    }
    if (selected === "terms") {
      navigate("/terms");
      return;
    }
    if (selected === "budget") {
      if (!hasBudget) {
        const uid = auth.currentUser?.uid;
        const chosen = uid ? localStorage.getItem(`viewMode:${uid}`) : null;
        if (chosen === "manual" || chosen === "smart") {
          setView("budget");
          return;
        }
        setView("wizard_choice");
        return;
      }
    }
    setView(selected);
  };

  async function handleEmailPasswordSignIn(email, password) {
    // 👇 tell the guards we’re in the auth flow (prevents mobile bounces during MFA)
    try { sessionStorage.setItem("trelli:authFlow", "1"); } catch {}

    try {
      // Try normal sign in first
      const cred = await signInWithEmailAndPassword(auth, email, password);
      // Force-refresh the user so emailVerified + enrolledFactors are up-to-date
      await cred.user.getIdToken(true);
      await cred.user.reload();

      // 👇 login done — clear the flag
      try { sessionStorage.removeItem("trelli:authFlow"); } catch {}
      return cred;
    } catch (err) {
      if (err?.code === "auth/multi-factor-auth-required") {
        const resolver = getMultiFactorResolver(auth, err);
        const hint = resolver.hints?.[0];
        if (!hint) throw new Error("This account requires a second factor, but none are enrolled.");

        // Ensure invisible reCAPTCHA exists
        let verifier = window._signinRecaptcha;
        if (!verifier) {
          window._signinRecaptcha = new RecaptchaVerifier(auth, "signin-recaptcha", { size: "invisible" });
          await window._signinRecaptcha.render();
          verifier = window._signinRecaptcha;
        }

        // Send SMS and collect code
        const provider = new PhoneAuthProvider(auth);
        const verificationId = await provider.verifyPhoneNumber(
          { multiFactorHint: hint, session: resolver.session },
          verifier
        );
        const code = window.prompt(`Enter the 6-digit code sent to ${hint.phoneNumber}:`);
        if (!code) throw new Error("Verification code required.");

        // Complete 2nd factor and **refresh user**
        const phoneCred = PhoneAuthProvider.credential(verificationId, code);
        const assertion = PhoneMultiFactorGenerator.assertion(phoneCred);
        const finalResult = await resolver.resolveSignIn(assertion);

        await finalResult.user.getIdToken(true);
        await finalResult.user.reload(); // <-- ensures enrolledFactors + emailVerified are current
        await refreshAuthSnapshot();

        // 👇 MFA done — clear the flag
        try { sessionStorage.removeItem("trelli:authFlow"); } catch {}
        return finalResult;
      }

      // 👇 on any error, clear the flag so you can retry without being “stuck” in auth mode
      try { sessionStorage.removeItem("trelli:authFlow"); } catch {}
      throw err;
    }
  }

  // === HARD GATE MODE (disable legacy step-by-step onboarding) ===
  const HARD_MFA_MODE = true;
  const HARD_MFA_NAV_REDIRECT_ENABLED = false; // keep false while stabilizing

  // A fresh, computed “must-fix-security” flag
  const mfaRequired =
    !isDev &&
    loggedIn &&
    checkedFresh &&
    (!freshEmailVerified || freshMfaCount === 0);

  // 🔒 In hard-gate mode: if user lacks email verification or MFA, always force Settings → Account
  useEffect(() => {
    if (!HARD_MFA_MODE) return;
    if (!HARD_MFA_NAV_REDIRECT_ENABLED) return;
    if (!loggedIn || checkingAuth) return;
    if (!checkedFresh) return;

    if (mfaRequired) {
      const here = (location.pathname || "/") + (location.search || "");
      const target = "/settings?tab=account";
      if (here !== target) {
        navigate(target, { replace: true });
        if (view !== "settings") setView("settings");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    HARD_MFA_MODE,
    HARD_MFA_NAV_REDIRECT_ENABLED,
    mfaRequired,
    loggedIn,
    checkingAuth,
    checkedFresh,
    location.pathname,
    location.search,
  ]);

  // These routes can be used before login; keep them after hooks but before other returns
  if (showAcceptInvite) return <AcceptInvitePage />;
  if (showAuthAssist) return <AuthAssist />;
  if (showTxDemo) return <TxActionDemo />;

  if (checkingAuth) return <div>Loading...</div>;
  if (!loggedIn) {
    // Public Terms page
    if (location.pathname.startsWith("/terms")) {
      return <TermsPage />;
    }
    if (location.pathname.startsWith("/privacy")) {
      return <PrivacyPage />;
    }
    // Show dedicated sign-up pages based on path
    if (location.pathname === "/signup") {
      return <SignUpChoice />;
    }
    if (location.pathname.startsWith("/signup/beta")) {
      return <SignUpBeta />;
    }
    // Default: login
    return (
      <LoginPage
        onLogin={() => setLoggedIn(true)}
        onSignIn={handleEmailPasswordSignIn}
      />
    );
  }

  // Dev-only preview for the daily email
  if (isDev && location.pathname.startsWith("/debug/daily-email")) {
    return <EmailPreview />;
  }

  // Logged-in: allow viewing Terms page
  if (location.pathname.startsWith("/terms")) {
    return <TermsPage />;
  }

  // Logged-in: allow viewing Privacy page
  if (location.pathname.startsWith("/privacy")) {
    return <PrivacyPage />;
  }

  // Logged-in: Terms consent step in the signup flow
  if (location.pathname.startsWith("/signup/terms")) {
    return <TermsConsent />;
  }

  // If a signed-in user navigates to the MFA enrollment step, show it first
  if (location.pathname.startsWith("/signup/mfa")) {
    return <SignUpMFA />;
  }

  // ⏳ Hold ALL protected pages until household bootstrap is done
  if (loggedIn && !bootstrapReady) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        Setting up your account…
      </div>
    );
  }

  console.debug("gate check (fresh)", {
    emailVerified: freshEmailVerified,
    mfaCount: freshMfaCount,
    view,
    loggedIn,
    checkingAuth,
    isDev
  });

  // --- Mobile “not ready yet” gate (enable per-view) ---
  const ALLOWED_MOBILE_VIEWS = new Set(["login", "dashboard", "settings", "goals", "rules", "transactions"]); // add more as you go
  const shouldBlockMobile = isMobile && loggedIn && !ALLOWED_MOBILE_VIEWS.has(view);

  // Small, branded notice shown on mobile for pages that aren't ready yet
  const MobileBlock = () => (
    // Fullscreen overlay to ensure the first tap doesn't hit anything behind the card
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "#f8f9fb",
        zIndex: 9500,              // above the sticky top bar (9000), below the idle modal (99999)
        overflow: "auto",
        WebkitOverflowScrolling: "touch",
      }}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div
        style={{
          maxWidth: 680,
          margin: "48px auto",
          padding: "20px 18px",
          background: "#fff",
          borderRadius: 14,
          border: "2px solid #F4C542",     // Trelli gold
          boxShadow: "0 12px 36px rgba(0,0,0,0.12)",
          textAlign: "left",
          lineHeight: 1.45,
        }}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            marginBottom: 10,
          }}
        >
          <div
            aria-hidden
            style={{
              width: 14,
              height: 14,
              borderRadius: 999,
              background: "#00B074",       // Trelli green
            }}
          />
          <div style={{ fontWeight: 900, fontSize: 18, color: "#00B074" }}>Beta notice</div>
        </div>

        <div style={{ color: "#222", marginBottom: 12 }}>
          This page isn’t mobile-friendly yet. Please use Trelli on a desktop while we optimize each page.
        </div>

        <div style={{ color: "#333", fontSize: 14, marginBottom: 16 }}>
          You can still use <strong>Dashboard</strong> on mobile today.
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            type="button"
            onPointerUp={(e) => {
              if (e.pointerType !== "mouse") {
                e.preventDefault();
                e.stopPropagation();
                navigate("/");
              }
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigate("/");
            }}
            onClick={(e) => {
              e.stopPropagation();
              navigate("/");
            }}
            style={{
              padding: "10px 14px",
              borderRadius: 10,
              border: "none",
              background: "#00B074",
              color: "#fff",
              fontWeight: 900,
              cursor: "pointer",
              WebkitTapHighlightColor: "transparent"
            }}
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Sidebar onSelect={handleSelect} />

      {loggedIn && normAccountId && (
        <NormalizationRebuildBanner
          minimized={normMinimized}
          onToggleMinimize={() => setNormMinimized((v) => !v)}
          accountId={normAccountId}
          job={normJob}
          lastTick={normLastTick}
        />
      )}

      <main
        className="trelli-page trelli-no-vw"
        style={{
          // Keep the visual offset for the fixed sidebar
          marginLeft: "var(--sidebar-offset, 175px)",
          // NEW: shrink main so margin + width == viewport width (prevents body overflow)
          width: "calc(100% - var(--sidebar-offset, 175px))",
          // Smooth when the sidebar collapses/expands
          transition: "margin-left 240ms ease, width 240ms ease",
          // Keep the rest exactly as you had
          overflowX: "hidden",
          minHeight: "100vh",
          paddingTop: "1rem",
          boxSizing: "border-box",   // defensive; ensures padding stays inside the width
          maxWidth: "100%"           // defensive; avoids accidental stretching by children
        }}
      >
        {/* Mobile top bar (shows “trelli”, centered). Stays under the hamburger. */}
        {isMobile && (
          <div
            style={{
              position: "sticky",
              top: 0,
              zIndex: 9000,              // below the hamburger (which is 11000)
              height: 48,
              display: "flex",
              alignItems: "right",
              justifyContent: "right",
              color: "#00B074",
              textAlign: "center",
              fontWeight: 800,
              fontSize: 26,
              background: "#fff",         // match your page background
              borderBottom: "1px solid #eee",
              // balance the left fixed hamburger so the text appears visually centered
              paddingLeft: 56,            // 40px button + a little cushion
              paddingRight: 35,           // symmetric so it’s truly centered
            }}
          >
            trelli
          </div>
        )}

        {/* HARD GATE banner: loud, sticky “set up 2FA / confirm email” */}
        {HARD_MFA_MODE && mfaRequired && (
          <OnboardingBanner
            phase="mfa_required"
            user={auth.currentUser}
            userDoc={userDoc}
            db={db}
            navigate={navigate}
            freshEmailVerified={freshEmailVerified}
            onResendEmail={async () => {
              try {
                await sendEmailVerification(auth.currentUser, { url: "https://app.usetrelli.com/" });
                alert("Verification email sent.");
              } catch (e) {
                console.error(e);
                alert("Could not send verification email.");
              }
            }}
          />
        )}

        {/* Hide legacy step-by-step setup while we’re in hard-gate mode */}
        {!HARD_MFA_MODE && ONBOARDING_LOCK_ENABLED && userDoc?.onboarding?.setupStep && userDoc.onboarding.setupStep !== "done" && (
          <OnboardingBanner
            phase="setup"
            user={auth.currentUser}
            userDoc={userDoc}
            db={db}
            navigate={navigate}
          />
        )}

        {/* Feature tour disabled while lock is off */}
        {ONBOARDING_LOCK_ENABLED &&
          userDoc?.onboarding?.featuresTour &&
          userDoc.onboarding.featuresTour !== "completed" &&
          userDoc.onboarding.featuresTour !== "dismissed" && (
          <OnboardingBanner
            phase="features"
            user={auth.currentUser}
            userDoc={userDoc}
            db={db}
            navigate={navigate}
          />
        )} 
        {shouldBlockMobile ? (
          <MobileBlock />
        ) : (
          <>
            {view === "dashboard" && <Dashboard />}
            {view === "budget" && <BudgetBucketsPage />}
            {view === "transactions" && <Transactions />}
            {view === "rules" && <RulesPage />}
            {view === "goals" && <GoalsPage />}
            {view === "reports" && <Reports />}
            {view === "settings" && <SettingsPage />}
            {view === "feedback" && <FeedbackInbox />}

            {view === "wizard_choice" && (
              <WizardChoice
                onManual={() => {
                  const uid = auth.currentUser?.uid;
                  if (uid) {
                    localStorage.setItem(`viewMode:${uid}`, "manual");
                    localStorage.setItem(`goalsComplete:${uid}`, "true");
                  }
                  setView("budget");
                }}
                onAuto={() => {
                  const uid = auth.currentUser?.uid;
                  if (uid) {
                    localStorage.setItem(`viewMode:${uid}`, "smart");
                    localStorage.setItem(`goalsComplete:${uid}`, "true");
                  }
                  setView("budget");
                }}
              />
            )}
            {view === "wizard" && <WizardShell onExit={() => setView("budget")} />}
          </>
        )}
        {view === "budget_loading" && <div>Loading budget…</div>}
        
        {/* Permanent footer */}
        <div style={{ height: 56 }} /> {/* spacer so content doesn’t hide behind footer */}
        <StickyFooter />  {/* ✅ NEW */}

        {/* 🔒 Inactivity modal */}
        {idleModalOpen && (
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="idle-title"
            style={{
              position: "fixed",
              zIndex: 99999,
              inset: 0,
              background: "rgba(0,0,0,0.45)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
            }}
            onClick={(e) => {
              // Ignore backdrop clicks; require an explicit button choice.
              e.stopPropagation();
            }}
          >
            <div
              style={{
                width: "min(560px, 92vw)",
                background: "#fff",
                borderRadius: 14,
                boxShadow: "0 12px 36px rgba(0,0,0,0.25)",
                border: "2px solid #F4C542",          // Trelli gold accent
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  background: "#00B074",               // Trelli green
                  color: "#fff",
                  padding: "14px 18px",
                  fontWeight: 900,
                  fontSize: 18,
                }}
                id="idle-title"
              >
                Are you still working on your financial freedom?
              </div>

              <div style={{ padding: "16px 18px", color: "#222", lineHeight: 1.45 }}>
                <p style={{ margin: "0 0 8px" }}>
                  For your security, you’ll be signed out if you’re inactive.
                </p>
                <p style={{ margin: 0, fontWeight: 800 }}>
                  In {countdown} seconds you will automatically be logged out.
                </p>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 12,
                  justifyContent: "flex-end",
                  padding: "12px 18px 18px",
                }}
              >
                <button
                  onClick={async () => {
                    try {
                      await signOut(auth);
                    } finally {
                      const uid = auth.currentUser?.uid || localStorage.getItem("trelli:lastUid");
                      if (uid) clearIdleDeadline(uid);
                      try { localStorage.removeItem("trelli:lastUid"); } catch {}
                      setIdleModalOpen(false);
                      setCountdown(COUNTDOWN_SECONDS);
                      setLastActiveAt(Date.now());
                    }
                  }}
                  style={{
                    padding: "10px 14px",
                    borderRadius: 10,
                    border: "1px solid #ddd",
                    background: "#fff",
                    fontWeight: 800,
                    cursor: "pointer",
                  }}
                >
                  No. Sign Out
                </button>
                <button
                  onClick={() => {
                    setIdleModalOpen(false);
                    setCountdown(COUNTDOWN_SECONDS);
                    setLastActiveAt(Date.now());
                    const uid = auth.currentUser?.uid;
                    if (uid) setIdleDeadline(uid); // extend the deadline since the user is active again
                  }}
                  style={{
                    padding: "10px 14px",
                    borderRadius: 10,
                    border: "none",
                    background: "#00B074",
                    color: "#fff",
                    fontWeight: 900,
                    cursor: "pointer",
                  }}
                >
                  Yes
                </button>
              </div>
            </div>
          </div>
        )}

      </main>
    </>
  );
}

function NormalizationRebuildBanner({ minimized, onToggleMinimize, accountId, job, lastTick }) {
  const processed = job?.processed ?? 0;
  const written = job?.written ?? 0;
  const skippedDeleted = job?.skippedDeleted ?? 0;

  const clampText = (s, max = 28) => {
    const str = String(s ?? "");
    if (str.length <= max) return str;
    return str.slice(0, max - 1) + "…";
  };

  const secondsSinceUpdate = Math.max(0, Math.floor((Date.now() - (job?.updatedAt?.toMillis?.() ?? lastTick)) / 1000));

  const containerStyle = {
    position: "fixed",
    right: 16,
    bottom: 16,
    zIndex: 9999,
    background: "#fff",
    border: "1px solid #e6e6e6",
    borderRadius: 12,
    boxShadow: "0 10px 30px rgba(0,0,0,0.10)",
    padding: minimized ? "10px 12px" : "12px 14px",
    width: minimized ? 220 : 360,
    fontSize: 13,
    color: "#111",
  };

  const titleStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    fontWeight: 800,
    marginBottom: minimized ? 0 : 6,
  };

  const accountSpanStyle = {
    fontFamily: "monospace",
    display: "inline-block",
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    verticalAlign: "bottom",
  };

  return (
    <div style={containerStyle}>
      <style>{`
        @keyframes trelliDots {
          0% { content: ""; }
          33% { content: "."; }
          66% { content: ".."; }
          100% { content: "..."; }
        }
        .trelli-dots::after {
          content: "";
          animation: trelliDots 1.2s infinite steps(3, end);
        }
      `}</style>

      <div style={titleStyle}>
        <div>
          Updating transactions <span className="trelli-dots" />
        </div>

        <button
          onClick={onToggleMinimize}
          title={minimized ? "Expand" : "Minimize"}
          style={{
            border: "none",
            background: "transparent",
            cursor: "pointer",
            fontSize: 18,
            lineHeight: "18px",
            padding: 4,
            marginLeft: 8,
            color: "#333",
          }}
        >
          −
        </button>
      </div>

      {!minimized && (
        <>
          <div style={{ marginBottom: 8, color: "#333" }}>
            This may take a few minutes.
            <br />
            This banner will automatically close when all transactions are updated.
          </div>

          <div style={{ color: "#555", marginBottom: 6 }}>
            Account:{" "}
            <span style={accountSpanStyle} title={accountId}>
              {clampText(accountId, 32)}
            </span>
          </div>

          <div style={{ color: "#777", fontSize: 12 }}>
            Last update: {secondsSinceUpdate <= 1 ? "just now" : `${secondsSinceUpdate}s ago`}
          </div>
        </>
      )}

      {minimized && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ fontSize: 12, color: "#555" }}>
            for{" "}
              <span style={accountSpanStyle} title={accountId}>
                {clampText(accountId, 24)}
              </span>
          </div>
        </div>
      )}
    </div>
  );
}

// 2) Export a top-level App that provides the Router
export default function App() {
  console.log("BOOT: App component constructing");
  // one-time, invisible reCAPTCHA for the sign-in flow
  useEffect(() => {
    // only create once
    if (!window._signinRecaptcha) {
      try {
        window._signinRecaptcha = new RecaptchaVerifier(
          auth,                // your initialized Auth
          "signin-recaptcha",  // container ID defined below
          { size: "invisible" }
        );
        // pre-render to avoid "element removed" errors
        window._signinRecaptcha.render().catch(() => {});
      } catch (e) {
        // ignore if already created or running in SSR
        // console.warn("recaptcha init skipped:", e);
      }
    }
  }, []);

  // --- Plaid OAuth return: auto-resume Link (runs once on load) ---
  useEffect(() => {
    // Only proceed if this is an OAuth return
    const params = new URLSearchParams(window.location.search);
    const isOAuthReturn = params.has("oauth_state_id");
    if (!isOAuthReturn) return;

    let cancelled = false;

    (async () => {
      try {
        // Ensure the Plaid Link script is available
        // (your index.html already loads it)
        if (!window.Plaid) {
          // Poll very briefly for the script to attach
          for (let i = 0; i < 20 && !window.Plaid; i++) {
            await new Promise(r => setTimeout(r, 50));
          }
        }
        if (!window.Plaid || cancelled) return;

        // Wait for Firebase Auth (current user needed for the token calls)
        const waitForAuth = async (timeoutMs = 8000) => {
          const start = Date.now();
          while (!cancelled) {
            const u = auth.currentUser;
            if (u) return u;
            if (Date.now() - start > timeoutMs) throw new Error("Auth not ready");
            await new Promise(r => setTimeout(r, 100));
          }
          throw new Error("Cancelled");
        };

        const user = await waitForAuth();
        if (cancelled) return;

        const idToken = await user.getIdToken(true);

        // Use the SAME link_token if we have it; otherwise request one.
        let linkToken = sessionStorage.getItem("plaid_link_token") || "";

        if (!linkToken) {
          // Fallback: create a fresh link_token (Plaid usually accepts this on OAuth return)
          const res = await fetch(PLAID_CREATE_URL, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({ mode: "create", daysRequested: 180 }),
          });
          if (!res.ok) {
            const msg = await res.text().catch(() => "Failed to create link token");
            throw new Error(`createLinkToken (resume) failed: ${msg}`);
          }
          const json = await res.json();
          linkToken = json.link_token;
        }

        // Cache the SAME token we are about to pass to Plaid.create (used to auto-resume after OAuth)
        sessionStorage.setItem("plaid_link_token", linkToken);

        // Initialize Plaid Link with receivedRedirectUri to complete OAuth
        const handler = window.Plaid.create({
          token: linkToken,
          // 👇 required for OAuth return so Link can finish the flow
          receivedRedirectUri: window.location.href,

          onSuccess: async (public_token, metadata) => {
            sessionStorage.removeItem("plaid_link_token"); // cleanup: no auto-resume needed anymore
            try {
              const exch = await fetch(PLAID_EXCHANGE_URL, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${idToken}`,
                },
                body: JSON.stringify({
                  public_token,
                  metadata,
                  mode: "create",
                }),
              });

              if (!exch.ok) {
                const msg = await exch.text().catch(() => "Exchange failed");
                console.error("exchangePublicToken failed:", msg);
                alert("We linked your bank, but there was a problem saving it. Please try again.");
              }
            } catch (e) {
              console.error("OAuth resume exchange error:", e);
              alert("We linked your bank, but there was a problem saving it. Please try again.");
            } finally {
              // Clean query string so we don't re-trigger on refresh
              try {
                const url = new URL(window.location.href);
                url.searchParams.delete("oauth_state_id");
                window.history.replaceState({}, "", url.toString());
              } catch {}
              // Clear the cached token if present
              sessionStorage.removeItem("plaid_link_token");
            }
          },

          onExit: () => {
            sessionStorage.removeItem("plaid_link_token"); // cleanup if the user/FI exits
            // If the user or FI exits, clean up and remove the OAuth param
            try {
              const url = new URL(window.location.href);
              url.searchParams.delete("oauth_state_id");
              window.history.replaceState({}, "", url.toString());
            } catch {}
          },
        });

        handler.open(); // <-- completes the OAuth flow
      } catch (err) {
        console.error("Plaid OAuth auto-resume failed:", err);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  // Hard bypass: if this tab is on /terms, render the Terms page standalone
  // so no onboarding or gating code mounts in this document at all.
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/terms")) {
    return <TermsPage />;
  }

  // FastTap: make taps on touch devices trigger immediately for common Trelli targets
  useEffect(() => {
    const isTouch = matchMedia?.("(hover: none) and (pointer: coarse)")?.matches;
    if (!isTouch) return;

    // Synthesize a click on touchend for clickable elements that aren't native <a>/<button>
    const onTouchEnd = (e) => {
      // If the browser already dispatched a click quickly, do nothing
      // Target only obvious Trelli click targets
      const el = e.target.closest(
        'button, a, [role="button"], [role="link"], .clickable, .nav-item, .menu-item'
      );
      if (!el) return;
      // Let native <a> and <button> behave as usual (your CSS already fixes their delay)
      if (el.tagName === "A" || el.tagName === "BUTTON") return;

      // Prevent the delayed "ghost" click and fire one now
      e.preventDefault();
      e.stopPropagation();
      el.click();
    };

    document.addEventListener("touchend", onTouchEnd, { passive: false });
    return () => document.removeEventListener("touchend", onTouchEnd);
  }, []);

  return (
    <Router>
      {/* Hidden container for the invisible reCAPTCHA used during MFA sign-in */}
      <div
        id="signin-recaptcha"
        style={{ position: "absolute", left: -9999, top: -9999, width: 1, height: 1, overflow: "hidden" }}
      />
      <AppInner />
    </Router>
  );
}